import React, { memo, useCallback } from "react";
import PropTypes from "prop-types";
import { DateTimeFieldsProvider } from "../../context/DateTimeFieldsContext";
import DateTimeFieldsContainer from "./DateTimeFieldsContainer";
import OpenDateField from "./OpenDateField";
import DueDateField from "./DueDateField";
import CloseDateField from "./CloseDateField";

/**
 * Content component that renders all date/time fields
 * Each field is a fully independent component following Single Responsibility Principle
 */
const AssignmentDateTimeFieldsContent = ({
  isEditMode,
  isDisabled,
  openDateRange,
  dueDateRange,
  closeDateRange,
  disabledTooltip,
  testId,
  onValidationError,
  isDueDatePassed,
  isCloseDatePassed,
}) => {
  // Memoized validation error handlers
  const handleOpenValidationError = useCallback(
    isInvalid => {
      if (onValidationError) {
        onValidationError("open", isInvalid);
      }
    },
    [onValidationError]
  );

  const handleDueValidationError = useCallback(
    isInvalid => {
      if (onValidationError) {
        onValidationError("due", isInvalid);
      }
    },
    [onValidationError]
  );

  const handleCloseValidationError = useCallback(
    isInvalid => {
      if (onValidationError) {
        onValidationError("close", isInvalid);
      }
    },
    [onValidationError]
  );

  return (
    <DateTimeFieldsContainer>
      <OpenDateField
        dateRange={openDateRange}
        isEditMode={isEditMode}
        isDisabled={isDisabled}
        disabledTooltip={disabledTooltip}
        testId={`${testId}-open-date`}
        onValidationError={handleOpenValidationError}
      />

      <DueDateField
        dateRange={dueDateRange}
        isEditMode={isEditMode}
        isDisabled={isDisabled}
        disabledTooltip={disabledTooltip}
        showError={isDueDatePassed}
        testId={`${testId}-due-date`}
        onValidationError={handleDueValidationError}
      />

      <CloseDateField
        dateRange={closeDateRange}
        isEditMode={isEditMode}
        isDisabled={isDisabled}
        disabledTooltip={disabledTooltip}
        showError={isCloseDatePassed}
        testId={`${testId}-close-date`}
        onValidationError={handleCloseValidationError}
      />
    </DateTimeFieldsContainer>
  );
};

const AssignmentDateTimeFields = ({
  openDate,
  dueDate,
  closeDate,
  visibilityDate,
  onDateChange,
  ...otherProps
}) => {
  // Handle date/time changes from context
  const handleChange = useCallback(
    (field, newDateTime, type) => {
      if (onDateChange) {
        onDateChange(field, newDateTime, type);
      }
    },
    [onDateChange]
  );

  return (
    <DateTimeFieldsProvider
      initialDates={{
        openDate,
        dueDate,
        closeDate,
        visibilityDate,
      }}
      onChange={handleChange}
    >
      <AssignmentDateTimeFieldsContent {...otherProps} />
    </DateTimeFieldsProvider>
  );
};

AssignmentDateTimeFields.propTypes = {
  /** Open date ISO string */
  openDate: PropTypes.string,
  /** Due date ISO string */
  dueDate: PropTypes.string,
  /** Close date ISO string */
  closeDate: PropTypes.string,
  /** Callback when any date changes - receives (field, newDateTime) */
  onDateChange: PropTypes.func,
  /** Whether fields are editable */
  isEditMode: PropTypes.bool,
  /** Whether fields are disabled */
  isDisabled: PropTypes.bool,
  /** Date range constraints for open date */
  openDateRange: PropTypes.object,
  /** Date range constraints for due date */
  dueDateRange: PropTypes.object,
  /** Date range constraints for close date */
  closeDateRange: PropTypes.object,
  /** Tooltip to show when disabled */
  disabledTooltip: PropTypes.node,
  /** Test ID prefix */
  testId: PropTypes.string,
  /** Callback when validation error occurs */
  onValidationError: PropTypes.func,
  /** Whether due date has passed */
  isDueDatePassed: PropTypes.bool,
  /** Whether close date has passed */
  isCloseDatePassed: PropTypes.bool,
};

AssignmentDateTimeFields.defaultProps = {
  openDate: "",
  dueDate: "",
  closeDate: "",
  onDateChange: null,
  isEditMode: false,
  isDisabled: false,
  openDateRange: null,
  dueDateRange: null,
  closeDateRange: null,
  visibilityDateRange: null,
  disabledTooltip: null,
  testId: "",
  onValidationError: null,
  isDueDatePassed: false,
  isCloseDatePassed: false,
};

export default memo(AssignmentDateTimeFields);
